import React, { useRef } from 'react';
import { useLiquidField } from '../hooks/useLiquidField';

const NODE_HEIGHT = 84;
const PANEL_FRAME = { x: 10, y: 4, w: 640, h: 490 };
const SLOTS = [0, 1, 2, 3, 4];
const EMPTY_SLOT = { x: 200, y: 208, w: 240 };
const FLOW_DURATION = '2s';
const TRAILS = [
  { className: 'diagram-trail-long', length: 34 },
  { className: 'diagram-trail-short', length: 14 },
];

// Each service gets its own picture of the work. Only development is about the tech stack.
export const DIAGRAMS = {
  development: {
    caption: 'The stack behind the work above.',
    description: 'A React web app and a Flutter mobile app connect to business logic in Laravel, which uses a MySQL database and Firebase for live updates, all running on Google Cloud.',
    frame: { ...PANEL_FRAME, label: 'Google Cloud', labelX: 32, labelY: 462 },
    nodes: [
      { x: 40, y: 30, w: 240, title: 'Web app', detail: 'React', lit: true },
      { x: 360, y: 30, w: 240, title: 'Mobile app', detail: 'Flutter', lit: true },
      { x: 180, y: 180, w: 280, title: 'Business logic', detail: 'Laravel', lit: true },
      { x: 40, y: 330, w: 240, title: 'Database', detail: 'MySQL', lit: true },
      { x: 360, y: 330, w: 240, title: 'Live updates', detail: 'Firebase', lit: true },
    ],
    links: [
      'M160 114 C160 150 280 142 280 180',
      'M480 114 C480 150 360 142 360 180',
      'M280 264 C280 300 160 292 160 330',
      'M360 264 C360 300 480 292 480 330',
      'M596 72 C640 150 640 260 596 372',
    ],
  },
  cloud: {
    caption: 'Where your product runs, and what we look after there.',
    description: 'Your product runs on Google Cloud, where we handle setup, scaling, running costs and upkeep.',
    frame: { x: 10, y: 140, w: 640, h: 354, label: 'Google Cloud', labelX: 32, labelY: 470 },
    nodes: [
      { x: 180, y: 16, w: 280, title: 'Your product', detail: 'Web and mobile apps' },
      { x: 40, y: 172, w: 270, title: 'Setup', detail: 'Built for your load', lit: true },
      { x: 350, y: 172, w: 270, title: 'Scaling', detail: 'Ready for busy days', lit: true },
      { x: 40, y: 318, w: 270, title: 'Running costs', detail: 'Kept affordable', lit: true },
      { x: 350, y: 318, w: 270, title: 'Upkeep', detail: 'We keep it running', lit: true },
    ],
    links: ['M320 100 L320 140'],
  },
  transformation: {
    caption: 'How a manual process becomes software your team uses.',
    description: 'We start from how work runs today, decide what moves to software first, build it, then roll it out with your team.',
    nodes: [
      { x: 40, y: 90, w: 240, title: 'Your processes', detail: 'How work runs today' },
      { x: 380, y: 90, w: 240, title: 'Priorities', detail: 'What moves first', lit: true },
      { x: 380, y: 310, w: 240, title: 'Build', detail: 'Software to fit', lit: true },
      { x: 40, y: 310, w: 240, title: 'Rollout', detail: 'With your team', lit: true },
    ],
    links: ['M280 132 L380 132', 'M500 174 L500 310', 'M380 352 L280 352'],
  },
  consulting: {
    caption: 'The three questions we look at, and what you leave with.',
    description: 'We review your architecture, security and cost, then give you clear next steps.',
    nodes: [
      { x: 30, y: 50, w: 270, title: 'Architecture', detail: 'Will it hold up?' },
      { x: 30, y: 208, w: 270, title: 'Security', detail: 'Where is the risk?' },
      { x: 30, y: 366, w: 270, title: 'Cost', detail: 'Is it worth it?' },
      { x: 375, y: 208, w: 255, title: 'Your next steps', detail: 'What to do first', lit: true },
    ],
    links: [
      'M300 92 C350 92 325 250 375 250',
      'M300 250 L375 250',
      'M300 408 C350 408 325 250 375 250',
    ],
  },
};

const ServiceDiagram = ({ diagram }) => {
  const { description, frame, links, nodes } = DIAGRAMS[diagram];
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  useLiquidField(canvasRef, { ambient: true, stillOnSmall: true });

  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // The sheen follows the pointer across the metal without re-rendering the diagram.
  const onPointerMove = (event) => {
    const rect = stageRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 660;
    const y = ((event.clientY - rect.top) / rect.height) * 500;
    stageRef.current.querySelectorAll('radialGradient').forEach((gradient, slot) => {
      const node = nodes[slot] || EMPTY_SLOT;
      gradient.setAttribute('cx', x - node.x);
      gradient.setAttribute('cy', y - node.y);
    });
  };

  const shownFrame = frame || PANEL_FRAME;

  return (
    <div className="diagram-stage" ref={stageRef} onPointerMove={onPointerMove}>
      <canvas className="diagram-liquid" ref={canvasRef} aria-hidden="true" />
      <svg className="service-diagram" viewBox="0 0 660 500" role="img" aria-label={description}>
        <defs>
          <linearGradient id="diagram-metal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3f6bff" />
            <stop offset="0.45" stopColor="#2450ff" />
            <stop offset="1" stopColor="#1638d6" />
          </linearGradient>
          <linearGradient id="diagram-edge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#cfdcff" stopOpacity="0.9" />
            <stop offset="0.55" stopColor="#cfdcff" stopOpacity="0" />
          </linearGradient>
          {SLOTS.map((slot) => (
            <radialGradient key={slot} id={`diagram-sheen-${slot}`} gradientUnits="userSpaceOnUse" cx="70" cy="-10" r="190">
              <stop offset="0" stopColor="#a9c4ff" stopOpacity="0.3" />
              <stop offset="1" stopColor="#a9c4ff" stopOpacity="0" />
            </radialGradient>
          ))}
        </defs>

        <rect
          className={`diagram-frame${frame ? '' : ' is-gone'}`}
          rx="26"
          style={{ x: shownFrame.x, y: shownFrame.y, width: shownFrame.w, height: shownFrame.h }}
        />
        {frame && (
          <text key={`${diagram}-label`} className="diagram-frame-label" x={frame.labelX} y={frame.labelY}>{frame.label}</text>
        )}

        <g key={diagram} className="diagram-links">
          {links.map((d, index) => {
            const id = `${diagram}-link-${index}`;
            const begin = `${index * -0.45}s`;
            return (
              <g key={id}>
                <path id={id} className="diagram-link" d={d} />
                {!reducedMotion && (
                  <>
                    {TRAILS.map(({ className, length }) => (
                      <path key={className} className={className} d={d} pathLength="100" strokeDasharray={`${length} 200`}>
                        <animate
                          attributeName="stroke-dashoffset"
                          values={`${length};${length - 100}`}
                          dur={FLOW_DURATION}
                          begin={begin}
                          repeatCount="indefinite"
                        />
                      </path>
                    ))}
                    <circle r="4" className="diagram-packet">
                      <animateMotion dur={FLOW_DURATION} begin={begin} repeatCount="indefinite">
                        <mpath href={`#${id}`} />
                      </animateMotion>
                    </circle>
                  </>
                )}
              </g>
            );
          })}
        </g>

        {/* Nodes stay mounted between services, so each box slides and resizes into its next position. */}
        {SLOTS.map((slot) => {
          const node = nodes[slot];
          const { x, y, w } = node || EMPTY_SLOT;
          const classes = `diagram-node${node && node.lit ? ' is-lit' : ''}${node ? '' : ' is-gone'}`;
          return (
            <g key={slot} className={classes} style={{ '--w': `${w}px`, transform: `translate(${x}px, ${y}px)` }}>
              <rect className="diagram-node-wall" y="5" height={NODE_HEIGHT} rx="14" />
              <rect className="diagram-node-base" height={NODE_HEIGHT} rx="14" />
              <rect className="diagram-node-metal" height={NODE_HEIGHT} rx="14" fill="url(#diagram-metal)" />
              <rect className="diagram-node-sheen" height={NODE_HEIGHT} rx="14" fill={`url(#diagram-sheen-${slot})`} />
              <rect className="diagram-node-edge" height={NODE_HEIGHT} rx="14" stroke="url(#diagram-edge)" />
              {node && (
                <g key={node.title} className="diagram-node-text">
                  <text className="diagram-node-title" x="20" y="37">{node.title}</text>
                  <text className="diagram-node-detail" x="20" y="62">{node.detail}</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default ServiceDiagram;
